import Page from '../../../components/PageWrapper';
import FuelQuotaBar from '../../../components/FuelQuotaBar';
import TabNav, { ITab } from '../../../components/tabs/TabNav';
import TabContent from '../../../components/tabs/TabContent';
import { useMemo, useState } from 'react';
import moment from 'moment';
import GTable from '../../../components/elements/GTable';
import { getPartnerFuelQuota } from '../../../api/requests';
import useUserAccount from '../../../hooks/userAccountHook';
import { useParams } from 'react-router-dom';
import Button from '../../../components/form-control/Button';
import useModal from '../../../hooks/modalHook';
import UpdatePartnerQuota from './UpdatePartnerQuota';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const tabs: ITab[] = [
  {
    id: 'top-up',
    title: 'Quota Top-up History'
  },
  {
    id: 'usage',
    title: 'Quota Usage History'
  }
];

const FuelQuota = () => {
  const { user } = useUserAccount();
  const [activeTab, setActiveTab] = useState('top-up');
  const [fuelQuotaStats, setFuelQuotaStats] = useState<any>();
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const { partnerId } = useParams();
  const partnerID = user.isInternalOwner() ? partnerId : user.company?.partner?.id;
  // const partnerID = user.isSuperAdmin() ? partnerId : user.company?.partner?.id;
  const queryClient = useQueryClient();

  const getFuelQuotaStats = async () => {
    if (!partnerID) return;
    try {
      const response = await getPartnerFuelQuota(partnerID);
      setFuelQuotaStats(response);
    } catch (error) {
      console.error(error);
    }
  };

  const { data: fuelQuotaData, isLoading: fuelQuotaLoading } = useQuery({
    queryKey: ['getFuelQuotaStats'],
    queryFn: getFuelQuotaStats
  });

  const topupColumns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Closing Balance',
            accessor: 'available',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Opening Balance',
            accessor: 'total',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Amount Bought',
            accessor: 'amount',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Cost per Litre ',
            accessor: 'costPerLitre',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Quantity (in Litres)',
            accessor: 'quantityInLitre',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },

          {
            Header: 'Date Time',
            accessor: 'loadDateTime',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? moment(row.cell.value).format('DD/MM/YY h:mm a') : '-'}
                </span>
              );
            }
          }
        ]
      }
    ],
    []
  );

  const usageColumns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Amount Bought',
            accessor: 'amount',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Cost per Litre ',
            accessor: 'costPerLitre',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Fuel (in Litres)',
            accessor: 'quantityInLitres',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Available',
            accessor: 'available',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Date Time',
            accessor: 'usedOn',
            Cell: (row: any) => {
              return (
                <span>
                  {row.cell.value ? moment(row.cell.value).format('DD/MM/YY h:mm a') : '-'}
                </span>
              );
            }
          }
        ]
      }
    ],
    []
  );

  const updatePartnerFuelQuota = () => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <UpdatePartnerQuota
          partnerId={partnerId}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getFuelQuotaStats'] });
          }}
          onClose={closeModal}
        />
      ),
      
      classes: '!p-0 r-[5px]'
    });
  };

  return (
    <Page title="Fuel Quota">
      <div className="bg-white w-full p-5 min-h-full">
        <div className="flex flex-col-reverse md:flex-row items-center justify-between ">
          <div className="">
            <h2 className="font-semibold text-3xl">Fuel Quota Status</h2>
            <span className="text-sm text-slate-400">
              View a list of all fuel quota purchases and associated details.{' '}
            </span>
          </div>
          {user.isInternalOwner() && (
            // {user.isSuperAdmin() && (
            <div className="">
              <Button label="Top up" bgColor="primary" curved onClick={updatePartnerFuelQuota} />
            </div>
          )}
        </div>

        <div className=" border p-5 mt-10">
          <FuelQuotaBar
            remainingQuantity={fuelQuotaStats?.currentBalance}
            totalQuantity={fuelQuotaStats?.loadBalance}
          />

          <div className="flex items-center justify-between flex-wrap mt-5">
            <div className="space-y-1">
              <span className="text-xs font-medium">
                Last Top-up :{' '}
                {fuelQuotaStats
                  ? moment(fuelQuotaStats?.updatedOn).format('DD-MM-YY h:mm a')
                  : '-'}
              </span>
              <h2 className="font-bold">
                {fuelQuotaStats?.loadHistory[0]?.quantityInLitre ?? '-'} LTRS
                <span className="text-xs ml-2">
                  (GHS {fuelQuotaStats?.currentCostPerLitre ?? '-'} per litre)
                </span>
              </h2>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium">Quota Remaining</span>
              <h2 className="font-bold">{fuelQuotaStats?.currentBalance ?? '-'} LTRS</h2>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium">Quota Balance</span>
              <h2 className="font-bold">{fuelQuotaStats?.loadBalance ?? '-'} LTRS</h2>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <TabNav
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={(id: string) => {
              setActiveTab(id);
            }}
          />
        </div>

        <div className="">
          <TabContent id={'top-up'} activeTab={activeTab}>
            <div className="overflow-x-auto">
              {fuelQuotaStats?.loadHistory && (
                <GTable
                  columns={topupColumns}
                  data={fuelQuotaStats?.loadHistory}
                  tableType={'quota'}
                  showSearch={false}
                  classes={'h-fit'}
                  loading={fuelQuotaLoading}
                />
              )}
            </div>
          </TabContent>
          <TabContent id={'usage'} activeTab={activeTab}>
            <div className="overflow-x-auto">
              {fuelQuotaStats?.usageHistory && (
                <GTable
                  columns={usageColumns}
                  data={fuelQuotaStats?.usageHistory}
                  tableType={'quota'}
                  showSearch={false}
                  classes={'h-fit'}
                  loading={fuelQuotaLoading}
                />
              )}
            </div>
          </TabContent>
        </div>

        <ModalComponent />
      </div>
    </Page>
  );
};

export default FuelQuota;
